<script setup lang="ts">
const { t } = useTexts();
const { setCurrentMenuOpenByGroup } = useAccountMenuStore();
const { userLimits } = useUserLimits();
const { fetchUserProfile } = useUserData();
const { userIsOnTimeoutActive, userIsOnExclusionActive } = useUserLimits();
const { isBlockedToChange: shoNextChange } = useAccountProtection();

const { isCentralizedExclusionEnabled, isUserV2Enabled } =
  useFeatureFlagsJson();

const { isAccountSetLimitsEnabled, isAccountTimeoutLimitsEnabled } =
  useFeatureFlagsNew();

useSeo({
  title: t('account.account_protection.title'),
});

const isCentralizedExclusionVisible = computed(() => {
  return (
    isAccountTimeoutLimitsEnabled.value && isCentralizedExclusionEnabled.value
  );
});

const getPillColor = (active: boolean) => {
  return active ? 'green' : 'gray';
};

function getPillText(active: boolean) {
  return active ? t('general.custom') : t('general.unlimited');
}

onMounted(async () => {
  // side effects do layout antigo — o UserV2ScreenAccountProtection cuida do
  // próprio fetch/abertura de accordion quando o flag está ligado
  if (isUserV2Enabled.value) {
    return;
  }

  setCurrentMenuOpenByGroup('user-account');

  if (!userLimits.value?.status) {
    await fetchUserProfile({ force: true });
  }
});

const { accordionToOpen } = useAccountProtection();

watch(accordionToOpen, async (newValue) => {
  // scroll/highlight do layout antigo — no V2 quem trata é o Screen
  if (isUserV2Enabled.value) {
    return;
  }

  if (newValue) {
    await sleep(200);

    const element = document.getElementById(newValue);

    if (!element) {
      return;
    }

    const MARGIN_ADD = 60;
    const rect = element.getBoundingClientRect();

    const elementTop = (rect?.top || 200) + document.body.scrollTop;

    const { headerHeight, topBarHeight } = useLayout();

    const scrollTop = ref(
      elementTop - MARGIN_ADD - headerHeight.value - topBarHeight.value,
    );

    scrollToTop(scrollTop.value);

    await sleep(500);

    element.style.opacity = '0.5';

    await sleep(200);

    element.style.opacity = '1';
  }
});
</script>

<template>
  <UserV2ScreenAccountProtection v-if="isUserV2Enabled" />
  <UserAccountPageLayout
    v-else
    on-back="user-account"
    :title="$t('account.account_protection.title')"
  >
    <UserBox>
      <div :class="$style.limitsText">
        <p
          v-if="isAccountSetLimitsEnabled"
          v-html="$t('account.account_protection.text_1')"
        ></p>
        <p
          v-else
          v-html="$t('account.account_protection.text_2')"
        ></p>
      </div>

      <div :class="$style.accordionContainer">
        <template v-if="isAccountSetLimitsEnabled">
          <WidgetsAccordion
            id="accordion-deposit"
            :pill-color="getPillColor(!!userLimits?.user_limit_deposit_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_deposit_active)"
            :show-icon="
              shoNextChange(
                userLimits?.user_limit_deposit_next_amount_change_requested_at,
              )
            "
            :title="$t('account.account_protection.user_limit_deposit')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_deposit')"
            />

            <UserAccountProtectionDepositLimit />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-bet"
            :pill-color="getPillColor(!!userLimits?.user_limit_bet_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_bet_active)"
            :show-icon="
              shoNextChange(
                userLimits?.user_limit_bet_next_amount_change_requested_at,
              )
            "
            :title="$t('account.account_protection.user_limit_bet')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_bet')"
            />

            <UserAccountProtectionBetLimit />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-loss"
            :pill-color="getPillColor(!!userLimits?.user_limit_loss_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_loss_active)"
            :show-icon="
              shoNextChange(
                userLimits?.user_limit_loss_next_amount_change_requested_at,
              )
            "
            :title="$t('account.account_protection.user_limit_loss')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_loss')"
            />

            <UserAccountProtectionLostLimit />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-time"
            :pill-color="getPillColor(!!userLimits?.user_limit_time_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_time_active)"
            :show-icon="shoNextChange(userLimits?.user_limit_time_change)"
            :title="$t('account.account_protection.user_limit_time')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_time')"
            />

            <UserAccountProtectionTimeLimit />
          </WidgetsAccordion>
        </template>

        <section>
          <WidgetsAccordion
            v-if="isAccountTimeoutLimitsEnabled"
            id="accordion-timeout"
            :pill-color="getPillColor(userIsOnTimeoutActive)"
            :pill-text="getPillText(userIsOnTimeoutActive)"
            :title="$t('account_menu.timeout_limits')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.timeout_limits.text')"
            />
            <UserAccountProtectionTimeoutLimits />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-exclusion"
            :pill-color="getPillColor(userIsOnExclusionActive)"
            :pill-text="getPillText(userIsOnExclusionActive)"
            :title="$t('account_menu.self_exclusion')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.self_exclusion.text')"
            />
            <UserAccountProtectionSelfExclusion />
          </WidgetsAccordion>

          <WidgetsAccordion
            v-if="isCentralizedExclusionVisible"
            id="accordion-centralized-exclusion"
            :title="$t('account_menu.centralized_self_exclusion')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.centralized_self_exclusion.text')"
            />
          </WidgetsAccordion>
        </section>
      </div>
    </UserBox>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.limitsText {
  @apply text-xs md:text-sm font-medium text-texts/90;

  p {
    @apply mb-5;
  }
}

.accordionContainer {
  @apply w-full flex flex-col mt-6 border-t border-texts/10 border-solid;

  .accordionItemDescription {
    @apply text-2xs md:text-xs text-texts/70 -mt-1 mb-4 flex items-center;
  }
}
</style>
